import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import './App.css';

// ==============================
// 1. AUTH & LAYOUT
// ==============================
import Login from './Login';
import ProtectedRoute from './ProtectedRoute';

// ==============================
// 2. DASHBOARDS & REPORTS
// ==============================
import Dashboard from './Dashboard';
import ReportDashboard from './ReportDashboard';
import RoughEstimate from './RoughEstimate';

// ==============================
// 3. CORE OPERATIONS (MFG)
// ==============================
import RoughPurchase from './RoughPurchase';
import RoughPurchaseHistory from './RoughPurchaseHistory';
import RoughStock from './RoughStock';
import StockSplitting from './StockSplitting';
import CutIssue from './CutIssue';
import MakeKapan from './MakeKapan';
import ProcessManager from './ProcessManager';
import AutoDept from './AutoDept';
import AutoJobWork from './AutoJobWork';

// ==============================
// 4. TRADING & EXTERNAL WORK
// ==============================
import RoughSale from './RoughSale';
import SalesHistory from './SalesHistory';
import DispatchJangad from './DispatchJangad';
import LaborManager from './LaborManager';
import GalaxyJobManager from './GalaxyJobManager';

// ==============================
// 5. FINANCE & ACCOUNTS
// ==============================
import AccountsPayable from './AccountsPayable';
import AccountsReceivable from './AccountsReceivable';
import BrokerLedger from './BrokerLedger';

// ==============================
// 6. MASTERS (SETTINGS & HR)
// ==============================
import UserManagement from './UserManagement'; 
import PartyMaster from './PartyMaster';
import BuyerMaster from './BuyerMaster';
import FactoryMaster from './FactoryMaster';
import BrokerMaster from './BrokerMaster';
import JobWorkerMaster from './JobWorkerMaster';    
import GalaxyPriceManager from './GalaxyPriceManager'; 
import HRMaster from './HRMaster';
import EmployeeMaster from './EmployeeMaster';
import MonthlyPayrollEntry from './MonthlyPayrollEntry';


function App() {
  const location = useLocation();

  // --- AUTH STATE ---
  // Initializes directly from session storage (synchronous)
  const [user, setUser] = useState(() => JSON.parse(sessionStorage.getItem('user')) || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
      // Small timeout to ensure smooth transition and prevent screen flashing
      setLoading(false);
  }, []);

  // --- LOGOUT HANDLER ---
  const handleLogout = () => {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      setUser(null);
      window.location.href = '/login';
  };

  // --- UI HELPERS ---
  const isActive = (path) => location.pathname === path ? 'active fw-bold text-white bg-primary' : '';
  const isParentActive = (paths) => paths.includes(location.pathname) ? 'active fw-bold text-white' : 'text-white-50';

  // --- KIOSK MODE CHECK ---
  const searchParams = new URLSearchParams(location.search);
  const isKioskMode = location.pathname === '/labor' || searchParams.get('mode') === 'kiosk';

  // Prevent rendering anything until auth check is fully complete
  if (loading) return null;

  return (
    <div className="App bg-light min-vh-100 d-flex flex-column">
      
      {/* ============================== */}
      {/* PREMIUM NAVBAR                 */}
      {/* ============================== */}
      {user && !isKioskMode && (
        <nav className="navbar navbar-expand-lg navbar-dark navbar-custom sticky-top d-print-none">
          <div className="container-fluid px-4">
            
            {/* BRAND LOGO */}
            <Link className="navbar-brand navbar-brand-custom d-flex align-items-center gap-2" to="/">
              <div className="bg-white text-dark rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{width:'38px', height:'38px', fontSize:'1.2rem'}}>💎</div>
              <div className="d-flex flex-column" style={{lineHeight: '1'}}>
                <span className="fw-bold fs-5">DiamondSys</span>
                <span style={{fontSize:'0.65rem', opacity: 0.7, letterSpacing:'1px'}}>ENTERPRISE ERP</span>
              </div>
            </Link>

            <button className="navbar-toggler border-0" type="button" data-bs-toggle="collapse" data-bs-target="#mainNav">
              <span className="navbar-toggler-icon"></span>
            </button>

            <div className="collapse navbar-collapse" id="mainNav">
              <ul className="navbar-nav ms-auto gap-1 align-items-center">
                
                {/* 1. HOME */}
                <li className="nav-item">
                  <Link className={`nav-link nav-link-custom ${isActive('/')}`} to="/">🏠 Dashboard</Link>
                </li>

                {/* 2. OPERATIONS */}
                <li className="nav-item dropdown">
                  <a className={`nav-link nav-link-custom dropdown-toggle ${isParentActive(['/inward', '/stock', '/split', '/auto', '/cuts', '/process', '/jobwork'])}`} href="#" role="button" data-bs-toggle="dropdown">
                    ⚙️ Operations
                  </a>
                  <ul className="dropdown-menu dropdown-menu-end dropdown-menu-custom shadow">
                    <li><h6 className="dropdown-header">Rough & Stock</h6></li>
                    <li><Link className="dropdown-item" to="/inward">➕ Rough Purchase</Link></li>
                    <li><Link className="dropdown-item" to="/rough-history">📜 Purchase History</Link></li>
                    <li><Link className="dropdown-item" to="/stock">📊 Stock Inventory</Link></li>
                    <li><Link className="dropdown-item" to="/split">✂️ Stock Splitting</Link></li>
                    <li><hr className="dropdown-divider" /></li>
                    <li><h6 className="dropdown-header">Manufacturing</h6></li>
                    <li><Link className="dropdown-item" to="/cuts">✨ Issue For MFG</Link></li>
                    <li><Link className="dropdown-item" to="/make-kapan">🔪 Make Kapans</Link></li>
                    <li><Link className="dropdown-item" to="/process">🏭 Factory Process</Link></li>
                    <li><Link className="dropdown-item" to="/jobwork">🤖 Auto Job Work</Link></li>
                    <li><Link className="dropdown-item" to="/auto">🔩 Consumables (Auto)</Link></li>
                  </ul>
                </li>

                {/* 3. TRADING */}
                <li className="nav-item dropdown">
                  <a className={`nav-link nav-link-custom dropdown-toggle ${isParentActive(['/sell', '/jangad', '/labor', '/history', '/galaxy-job'])}`} href="#" role="button" data-bs-toggle="dropdown">
                    ⚖️ Trading
                  </a>
                  <ul className="dropdown-menu dropdown-menu-end dropdown-menu-custom shadow">
                    <li><h6 className="dropdown-header">Sales</h6></li>
                    <li><Link className="dropdown-item" to="/sell">💰 Sell Rough</Link></li>
                    <li><Link className="dropdown-item" to="/history">📜 Sales History</Link></li>
                    <li><hr className="dropdown-divider" /></li>
                    <li><h6 className="dropdown-header">External Work</h6></li>
                    <li><Link className="dropdown-item" to="/jangad">📦 Issue Jangad</Link></li>
                    <li><Link className="dropdown-item" to="/labor">👷 Labor Manager</Link></li>
                    <li><Link className="dropdown-item" to="/galaxy-job">🌌 Galaxy Dept</Link></li>
                  </ul>
                </li>

                {/* 4. FINANCE */}
                <li className="nav-item dropdown">
                  <a className={`nav-link nav-link-custom dropdown-toggle ${isParentActive(['/padtar-calculator','/pay', '/collect', '/broker-ledger'])}`} href="#" role="button" data-bs-toggle="dropdown">
                    💵 Finance
                  </a>
                  <ul className="dropdown-menu dropdown-menu-end dropdown-menu-custom shadow">
                    <li><Link className="dropdown-item" to="/padtar-calculator">🧮 Padtar Estimator</Link></li>
                    <li><Link className="dropdown-item" to="/pay">📤 Accounts Payable</Link></li>
                    <li><Link className="dropdown-item" to="/collect">📥 Accounts Receivable</Link></li>
                    <li><Link className="dropdown-item" to="/broker-ledger">📘 Broker Ledger</Link></li>
                  </ul>
                </li>

                {/* 5. MASTERS */}
                <li className="nav-item dropdown">
                  <a className={`nav-link nav-link-custom dropdown-toggle ${isParentActive(['/parties', '/buyers', '/factories', '/broker-master', '/job-workers', '/galaxy-rates', '/users', '/hr-masters', '/employee-master', '/payroll-entry'])}`} href="#" role="button" data-bs-toggle="dropdown">
                    👥 Masters
                  </a>
                  <ul className="dropdown-menu dropdown-menu-end dropdown-menu-custom shadow">
                    <li><h6 className="dropdown-header">Admin</h6></li>
                    <li><Link className="dropdown-item" to="/users">🔐 User Manager</Link></li>
                    <li><hr className="dropdown-divider" /></li>
                    <li><h6 className="dropdown-header">Contacts</h6></li>
                    <li><Link className="dropdown-item" to="/parties">Parties (Suppliers)</Link></li>
                    <li><Link className="dropdown-item" to="/buyers">Buyers (Customers)</Link></li>
                    <li><Link className="dropdown-item" to="/broker-master">Brokers</Link></li>
                    <li><hr className="dropdown-divider" /></li>
                    <li><h6 className="dropdown-header">Factory Setup</h6></li>
                    <li><Link className="dropdown-item" to="/hr-masters">HR Master</Link></li>
                    <li><Link className="dropdown-item" to="/employee-master">Employee Master</Link></li>
                    <li><Link className="dropdown-item" to="/payroll-entry">Payroll Entry</Link></li>
                    <li><Link className="dropdown-item" to="/job-workers">Job Workers</Link></li>
                    <li><Link className="dropdown-item" to="/galaxy-rates">Galaxy Rates</Link></li>
                    <li><Link className="dropdown-item" to="/factories">Internal Factories</Link></li>
                  </ul>
                </li>

                {/* 6. REPORTS */}
                <li className="nav-item">
                  <Link className={`nav-link nav-link-custom ${isActive('/reports')}`} to="/reports">📈 Reports</Link>
                </li>
              </ul>
              
              {/* USER PROFILE BADGE */}
              <div className="d-flex align-items-center ms-lg-4 mt-3 mt-lg-0">
                  <div className="user-badge d-flex align-items-center gap-2">
                      <div className="d-flex flex-column text-end" style={{lineHeight:'1.1'}}>
                        <span className="text-white fw-bold small">{user.fullName || user.username}</span>
                        <span className="text-white-50" style={{fontSize:'0.7rem'}}>
                             {user.permissions?.userManageModule ? 'ADMIN' : 'USER'}
                        </span>
                      </div>
                      <button 
                        onClick={handleLogout} 
                        className="btn btn-danger btn-sm rounded-circle d-flex align-items-center justify-content-center shadow" 
                        style={{width:'32px', height:'32px'}}
                        title="Logout"
                      >
                        <span style={{fontSize:'0.8rem'}}>⏻</span>
                      </button>
                  </div>
              </div>

            </div>
          </div>
        </nav>
      )}

      {/* ============================== */}
      {/* ROUTING / CONTENT AREA           */}
      {/* ============================== */}
      <div className={`flex-grow-1 ${isKioskMode ? "" : "container py-4"}`}>
        <Routes>
          
          <Route path="/login" element={<Login setAuth={setUser} />} />

          {/* --- DASHBOARD (Office Access Only) --- */}
          <Route element={<ProtectedRoute user={user} />}> 
               <Route path="/" element={user?.access?.office ? <Dashboard /> : <Navigate to="/login" />} />
               <Route path="/reports" element={<ReportDashboard />} />
          </Route>

          {/* --- 1. ROUGH MODULE --- */}
          <Route element={<ProtectedRoute user={user} requiredPermission="roughModule" />}>
              <Route path="/inward" element={<RoughPurchase />} />
              <Route path="/rough-history" element={<RoughPurchaseHistory />} />
              <Route path="/stock" element={<RoughStock />} />
              <Route path="/split" element={<StockSplitting />} />
          </Route>

          {/* --- 2. MFG / AUTO MODULE --- */}
          <Route element={<ProtectedRoute user={user} requiredPermission="mfgModule" />}>
              <Route path="/cuts" element={<CutIssue />} />
              <Route path="/make-kapan" element={<MakeKapan />} />
              <Route path="/jobwork" element={<AutoJobWork />} />
              <Route path="/auto" element={<AutoDept />} /> 
          </Route>

          {/* --- 3. FACTORY / LABOR MODULE --- */}
          <Route element={<ProtectedRoute user={user} requiredPermission="factoryModule" />}>
              <Route path="/process" element={<ProcessManager />} />
              <Route path="/labor" element={<LaborManager />} />
          </Route>
          
          {/* --- 4. GALAXY MODULE --- */}
          <Route element={<ProtectedRoute user={user} requiredPermission="galaxyModule" />}>
               <Route path="/galaxy-job" element={<GalaxyJobManager />} />
          </Route>

          {/* --- 5. SALES MODULE --- */}
          <Route element={<ProtectedRoute user={user} requiredPermission="salesModule" />}>
              <Route path="/sell" element={<RoughSale />} />
              <Route path="/history" element={<SalesHistory />} />
              <Route path="/jangad" element={<DispatchJangad />} />
          </Route>

          {/* --- 6. FINANCE MODULE --- */}
          <Route element={<ProtectedRoute user={user} requiredPermission="financeModule" />}>
              {/* 🎯 NEW CALCULATOR ROUTE */}
              <Route path="/padtar-calculator" element={<RoughEstimate />} />
              <Route path="/pay" element={<AccountsPayable />} />
              <Route path="/collect" element={<AccountsReceivable />} />
              <Route path="/broker-ledger" element={<BrokerLedger />} />
          </Route>

          {/* --- 7. MASTERS (Combined ALL Master Routes) --- */}
          <Route element={<ProtectedRoute user={user} requiredPermission="masterModule" />}>
              {/* Contacts */}
              <Route path="/parties" element={<PartyMaster />} />
              <Route path="/buyers" element={<BuyerMaster />} />
              <Route path="/broker-master" element={<BrokerMaster />} />
              
              {/* Factory Setup */}
              <Route path="/factories" element={<FactoryMaster />} />
              <Route path="/job-workers" element={<JobWorkerMaster />} />
              <Route path="/galaxy-rates" element={<GalaxyPriceManager />} />
              
              {/* HR & Payroll */}
              <Route path="/hr-masters" element={<HRMaster />} />
              <Route path="/employee-master" element={<EmployeeMaster />} />
              <Route path="/payroll-entry" element={<MonthlyPayrollEntry />} />
          </Route>

          {/* --- 8. SUPER ADMIN --- */}
          <Route element={<ProtectedRoute user={user} requiredPermission="userManageModule" />}>
               <Route path="/users" element={<UserManagement />} />
          </Route>

          {/* Catch All / Fallback */}
          <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />

        </Routes>
      </div>

      {/* ============================== */}
      {/* FOOTER                         */}
      {/* ============================== */}
      {user && !isKioskMode && (
        <footer className="bg-white text-center py-3 text-muted small border-top mt-auto d-print-none">
          <div className="container">
            &copy; {new Date().getFullYear()} <strong>DiamondSys ERP</strong>
          </div>
        </footer>
      )}

    </div>
  );
}

export default App;